import { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Users,
  Shield,
  Crown,
  UserCog,
  Eye,
  Mail,
  Search,
  AlertCircle,
  UserPlus,
  MoreHorizontal,
  Check,
} from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import api from '../../lib/api';
import InviteMemberModal from '../../components/InviteMemberModal';
import Modal from '../../components/ui/Modal';

interface TeamMember {
  id: string;
  name: string;
  email: string;
  roleInOrg: 'OWNER' | 'ADMIN' | 'MANAGER' | 'STAFF' | 'VIEWER';
  createdAt: string;
  _count?: {
    jobAssignments: number;
  };
}

const ROLE_CONFIG = {
  OWNER: {
    icon: Crown,
    color: 'text-purple-500 dark:text-purple-300',
    bg: 'bg-purple-500/15',
    border: 'border-purple-500/30',
    label: 'Owner',
    description: 'Full system access and ownership',
  },
  ADMIN: {
    icon: Shield,
    color: 'text-red-500 dark:text-red-300',
    bg: 'bg-red-500/15',
    border: 'border-red-500/30',
    label: 'Admin',
    description: 'All permissions except ownership transfer',
  },
  MANAGER: {
    icon: UserCog,
    color: 'text-accentText',
    bg: 'bg-accent2/15',
    border: 'border-accent2/30',
    label: 'Manager',
    description: 'Manage jobs and assign staff',
  },
  STAFF: {
    icon: Users,
    color: 'text-emerald-500 dark:text-emerald-300',
    bg: 'bg-emerald-500/15',
    border: 'border-emerald-500/30',
    label: 'Staff',
    description: 'Work on assigned jobs',
  },
  VIEWER: {
    icon: Eye,
    color: 'text-glass-text-2',
    bg: 'bg-glass-3',
    border: 'border-glass',
    label: 'Viewer',
    description: 'Read-only access',
  },
};

const ROLE_ORDER = ['OWNER', 'ADMIN', 'MANAGER', 'STAFF', 'VIEWER'];

export default function Team() {
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState<string>('ALL');
  const [changingRole, setChangingRole] = useState<string | null>(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [roleSheetMember, setRoleSheetMember] = useState<TeamMember | null>(null);

  useEffect(() => {
    fetchTeamMembers();
  }, []);

  const fetchTeamMembers = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/organizations/${user?.organizationId}/members`);
      setMembers(response.data);
    } catch (error) {
      console.error('Failed to fetch team members:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (memberId: string, newRole: string) => {
    if (!confirm(`Are you sure you want to change this member's role to ${ROLE_CONFIG[newRole as keyof typeof ROLE_CONFIG].label}?`)) {
      return;
    }

    try {
      setChangingRole(memberId);
      await api.patch(`/users/${memberId}`, { roleInOrg: newRole });
      await fetchTeamMembers();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to update role');
    } finally {
      setChangingRole(null);
    }
  };

  const handleInviteSuccess = async () => {
    setShowInviteModal(false);
    await fetchTeamMembers();
    await queryClient.invalidateQueries({ queryKey: ['agency', 'dashboard'] });
  };

  const canManageRoles = user?.roleInOrg === 'OWNER' || user?.roleInOrg === 'ADMIN';
  const canInviteMembers = ['OWNER', 'ADMIN', 'MANAGER'].includes(user?.roleInOrg || '');

  const filteredMembers = members
    .filter((member) => {
      const matchesSearch =
        member.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        member.email.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesRole = selectedRole === 'ALL' || member.roleInOrg === selectedRole;
      return matchesSearch && matchesRole;
    })
    .sort((a, b) => {
      const roleA = ROLE_ORDER.indexOf(a.roleInOrg);
      const roleB = ROLE_ORDER.indexOf(b.roleInOrg);
      return roleA - roleB;
    });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent2 mx-auto"></div>
          <p className="mt-4 text-glass-text-2">Loading team members...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 md:items-center">
        <div className="min-w-0">
          <h1 className="text-[1.375rem] font-semibold tracking-tightest text-glass-text md:text-[2.25rem]">Team Management</h1>
          <p className="mt-1 text-xs text-glass-text-2 md:text-base">Manage your organization's members and their roles</p>
        </div>
        {canInviteMembers && (
          <button
            data-help-id="team.invite_member_button"
            onClick={() => setShowInviteModal(true)}
            className="btn-accent inline-flex h-11 shrink-0 items-center gap-2 px-3 text-sm font-medium md:h-10 md:px-4"
          >
            <UserPlus className="w-5 h-5" />
            <span className="md:hidden">Invite</span>
            <span className="hidden md:inline">Invite Member</span>
          </button>
        )}
      </div>

      {/* Stats */}
      <div className="hidden grid-cols-5 gap-4 md:grid">
        {ROLE_ORDER.map((role) => {
          const config = ROLE_CONFIG[role as keyof typeof ROLE_CONFIG];
          const Icon = config.icon;
          const count = members.filter((m) => m.roleInOrg === role).length;

          return (
            <div
              key={role}
              className={`glass-card p-4 cursor-pointer transition-all hover:border-accent2/40 ${
                selectedRole === role ? 'ring-2 ring-accent2' : ''
              }`}
              onClick={() => setSelectedRole(selectedRole === role ? 'ALL' : role)}
            >
              <div className="flex items-center gap-2 mb-2">
                <Icon className={`w-5 h-5 ${config.color}`} />
                <span className={`text-[0.7rem] font-medium uppercase tracking-[0.05em] ${config.color}`}>
                  {config.label}
                </span>
              </div>
              <div className="text-2xl font-semibold text-glass-text tracking-tightest">{count}</div>
            </div>
          );
        })}
      </div>

      {/* Mobile role filters */}
      <div
        className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1 md:hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        aria-label="Filter team members by role"
      >
        <button
          type="button"
          onClick={() => setSelectedRole('ALL')}
          className={`inline-flex min-h-11 shrink-0 cursor-pointer items-center gap-1.5 rounded-full border px-3 text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent2/50 ${
            selectedRole === 'ALL'
              ? 'border-accent2/40 bg-accent2/15 font-semibold text-accentText'
              : 'border-glass bg-glass-2 font-medium text-glass-text-2 hover:bg-glass-3'
          }`}
          aria-pressed={selectedRole === 'ALL'}
        >
          <Users className="h-4 w-4" />
          <span>All</span>
          <span className="text-[11px] opacity-75">{members.length}</span>
        </button>
        {ROLE_ORDER.map((role) => {
          const config = ROLE_CONFIG[role as keyof typeof ROLE_CONFIG];
          const Icon = config.icon;
          const count = members.filter((member) => member.roleInOrg === role).length;
          const active = selectedRole === role;

          return (
            <button
              key={role}
              type="button"
              onClick={() => setSelectedRole(active ? 'ALL' : role)}
              className={`inline-flex min-h-11 shrink-0 cursor-pointer items-center gap-1.5 rounded-full border px-3 text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent2/50 ${
                active
                  ? 'border-accent2/40 bg-accent2/15 font-semibold'
                  : 'border-glass bg-glass-2 font-medium hover:bg-glass-3'
              }`}
              aria-pressed={active}
            >
              <Icon className={`h-4 w-4 ${config.color}`} />
              <span className={config.color}>{config.label}</span>
              <span className="text-[11px] text-glass-text-3">{count}</span>
            </button>
          );
        })}
      </div>

      {/* Filters */}
      <div className="glass-card p-3 md:p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-glass-text-3 w-5 h-5" />
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="glass-input h-11 w-full pl-10 pr-4 text-sm md:h-10"
              aria-label="Search team members"
            />
          </div>
          <select
            value={selectedRole}
            onChange={(e) => setSelectedRole(e.target.value)}
            className="glass-input hidden h-10 px-4 text-sm md:block"
          >
            <option value="ALL">All Roles</option>
            {ROLE_ORDER.map((role) => (
              <option key={role} value={role}>
                {ROLE_CONFIG[role as keyof typeof ROLE_CONFIG].label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Permission Notice */}
      {!canManageRoles && (
        <div className="glass-card flex items-start gap-3 border-amber-500/30 bg-amber-500/10 p-3 md:p-4">
          <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-600 dark:text-amber-200">View-Only Mode</p>
            <p className="text-sm text-glass-text-2 mt-1">
              Only Owners and Admins can modify team member roles.
            </p>
          </div>
        </div>
      )}

      {/* Mobile team members list */}
      <div className="glass-card overflow-hidden md:hidden">
        {filteredMembers.length === 0 ? (
          <div className="px-4 py-12 text-center">
            <Users className="mx-auto mb-3 h-10 w-10 text-glass-text-3" />
            <p className="text-sm font-medium text-glass-text">No team members found</p>
            <p className="mt-1 text-xs text-glass-text-2">Try adjusting your search or filters</p>
          </div>
        ) : (
          <div className="divide-y divide-glass">
            {filteredMembers.map((member) => {
              const roleConfig = ROLE_CONFIG[member.roleInOrg];
              const isCurrentUser = member.id === user?.id;
              const joined = new Date(member.createdAt).toLocaleDateString(undefined, {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
              });

              return (
                <div key={member.id} className="flex min-h-[72px] items-center gap-2.5 px-[13px] py-2">
                  <div
                    className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full border ${roleConfig.bg} ${roleConfig.border}`}
                    aria-hidden="true"
                  >
                    <span className={`text-sm font-semibold ${roleConfig.color}`}>
                      {member.name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex min-w-0 items-center gap-1.5">
                      <span className="truncate text-sm font-semibold text-glass-text">{member.name}</span>
                      {isCurrentUser && (
                        <span className="shrink-0 rounded-full bg-accent2/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.05em] text-accentText">
                          You
                        </span>
                      )}
                    </div>
                    <div className="mt-0.5 flex min-w-0 items-center gap-1 text-[11px] text-glass-text-3">
                      <Mail className="h-[11px] w-[11px] shrink-0" />
                      <span className="truncate">{member.email}</span>
                    </div>
                    <div className="mt-0.5 flex min-w-0 items-center gap-1 text-[11px] text-glass-text-3">
                      <span className={`shrink-0 font-medium ${roleConfig.color}`}>{roleConfig.label}</span>
                      <span aria-hidden="true">·</span>
                      <span className="shrink-0">{member._count?.jobAssignments || 0} jobs</span>
                      <span aria-hidden="true">·</span>
                      <span className="truncate">Joined {joined}</span>
                    </div>
                  </div>
                  {canManageRoles && !isCurrentUser && (
                    <button
                      type="button"
                      onClick={() => setRoleSheetMember(member)}
                      disabled={changingRole === member.id}
                      className="inline-flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-lg border border-glass bg-glass-2 text-glass-text-2 transition-colors hover:border-accent2/40 hover:bg-glass-3 hover:text-accentText focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent2/50 disabled:cursor-not-allowed disabled:opacity-50"
                      aria-label={`Change role for ${member.name}`}
                      aria-haspopup="dialog"
                    >
                      <MoreHorizontal className="h-[18px] w-[18px]" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Desktop team members table */}
      <div className="hidden glass-card overflow-hidden md:block">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-glass-1 border-b border-glass">
              <tr>
                <th className="px-6 py-3 text-left text-[0.7rem] font-medium text-glass-text-2 uppercase tracking-[0.05em]">
                  Member
                </th>
                <th className="px-6 py-3 text-left text-[0.7rem] font-medium text-glass-text-2 uppercase tracking-[0.05em]">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-[0.7rem] font-medium text-glass-text-2 uppercase tracking-[0.05em]">
                  Assigned Jobs
                </th>
                <th className="px-6 py-3 text-left text-[0.7rem] font-medium text-glass-text-2 uppercase tracking-[0.05em]">
                  Joined
                </th>
                {canManageRoles && (
                  <th className="px-6 py-3 text-left text-[0.7rem] font-medium text-glass-text-2 uppercase tracking-[0.05em]">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-glass">
              {filteredMembers.map((member) => {
                const roleConfig = ROLE_CONFIG[member.roleInOrg];
                const Icon = roleConfig.icon;
                const isCurrentUser = member.id === user?.id;

                return (
                  <tr key={member.id} className="hover:bg-glass-3 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full ${roleConfig.bg} border ${roleConfig.border} flex items-center justify-center`}>
                          <span className={`text-lg font-semibold ${roleConfig.color}`}>
                            {member.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <div className="font-medium text-glass-text flex items-center gap-2">
                            {member.name}
                            {isCurrentUser && (
                              <span className="bg-accent2/15 px-2 py-0.5 text-[0.65rem] font-medium uppercase tracking-[0.05em] text-accentText">
                                You
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-glass-text-2 flex items-center gap-1">
                            <Mail className="w-3 h-3" />
                            {member.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Icon className={`w-4 h-4 ${roleConfig.color}`} />
                        <div>
                          <div className={`text-sm font-medium ${roleConfig.color}`}>
                            {roleConfig.label}
                          </div>
                          <div className="text-xs text-glass-text-2">{roleConfig.description}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-glass-text">
                        {member._count?.jobAssignments || 0} jobs
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-glass-text-2">
                        {new Date(member.createdAt).toLocaleDateString()}
                      </div>
                    </td>
                    {canManageRoles && (
                      <td className="px-6 py-4">
                        {isCurrentUser ? (
                          <span className="text-xs text-glass-text-3 italic">Cannot modify own role</span>
                        ) : (
                          <select
                            value={member.roleInOrg}
                            onChange={(e) => handleRoleChange(member.id, e.target.value)}
                            disabled={changingRole === member.id}
                            className="glass-input text-sm h-8 px-2 disabled:opacity-50"
                          >
                            {ROLE_ORDER.map((role) => (
                              <option key={role} value={role}>
                                {ROLE_CONFIG[role as keyof typeof ROLE_CONFIG].label}
                              </option>
                            ))}
                          </select>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {filteredMembers.length === 0 && (
          <div className="text-center py-12">
            <Users className="w-12 h-12 text-glass-text-3 mx-auto mb-3" />
            <p className="text-glass-text">No team members found</p>
            <p className="text-sm text-glass-text-2 mt-1">Try adjusting your search or filters</p>
          </div>
        )}
      </div>

      {/* Mobile role action sheet */}
      <Modal
        isOpen={!!roleSheetMember}
        onClose={() => setRoleSheetMember(null)}
        title={roleSheetMember ? `Change role · ${roleSheetMember.name}` : 'Change role'}
        size="sm"
        mobilePresentation="sheet"
      >
        {roleSheetMember && (
          <div className="space-y-1">
            {ROLE_ORDER.map((role) => {
              const config = ROLE_CONFIG[role as keyof typeof ROLE_CONFIG];
              const Icon = config.icon;
              const isSelected = roleSheetMember.roleInOrg === role;

              return (
                <button
                  key={role}
                  type="button"
                  onClick={() => {
                    const memberId = roleSheetMember.id;
                    setRoleSheetMember(null);
                    void handleRoleChange(memberId, role);
                  }}
                  disabled={isSelected || changingRole === roleSheetMember.id}
                  className={`flex min-h-11 w-full items-center gap-3 rounded-lg px-3 py-1.5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent2/50 disabled:cursor-default ${
                    isSelected ? 'bg-glass-3' : 'cursor-pointer hover:bg-glass-3'
                  }`}
                  aria-current={isSelected ? 'true' : undefined}
                >
                  <span
                    className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border ${config.bg} ${config.border}`}
                    aria-hidden="true"
                  >
                    <Icon className={`h-4 w-4 ${config.color}`} />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className={`block text-sm font-semibold ${config.color}`}>{config.label}</span>
                    <span className="block truncate text-[11px] text-glass-text-3">{config.description}</span>
                  </span>
                  {isSelected && <Check className="h-4 w-4 shrink-0 text-accentText" />}
                </button>
              );
            })}
          </div>
        )}
      </Modal>

      {/* Invite Member Modal */}
      {showInviteModal && (
        <InviteMemberModal
          organizationId={user?.organizationId || ''}
          onClose={() => setShowInviteModal(false)}
          onSuccess={handleInviteSuccess}
        />
      )}
    </div>
  );
}

